import React, { Component } from 'react';
import Axios from 'axios';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { estados } from '../../util/estados';

// Tipos de análise de insumos (espelha MapaFornecedor/FormFilters.jsx).
const TIPOS_ANALISE = [
    { value: 'Por padrão', label: 'Por padrão' },
    { value: 'Por fase de obra', label: 'Por fase de obra' },
    { value: 'Por Incorporadora', label: 'Por Incorporadora' },
];

// Fase de obra e padrão (espelham MapaFornecedor/FilterByType.jsx).
const FASES_OBRA = [
    { value: 'Planta', label: 'Planta' },
    { value: 'Construção', label: 'Construção' },
    { value: 'Pronto', label: 'Pronto' },
];

const PADROES = [
    { value: 'Compacto', label: 'Compacto' },
    { value: 'Econômico', label: 'Econômico' },
    { value: 'Standard', label: 'Standard' },
    { value: 'Médio', label: 'Médio' },
    { value: 'Alto', label: 'Alto' },
    { value: 'Luxo', label: 'Luxo' },
    { value: 'Super Luxo', label: 'Super Luxo' },
];

/**
 * Painel de filtros do Mapa de Fornecedores (Mapbox). Componente controlado: o pai
 * (MapaFornecedorMapbox) detém o estado e recebe as mudanças via onChange(campo, valor).
 *
 * Reúne numa única coluna os dois formulários do mapa Google — a barra lateral
 * (MapaFornecedor/FormFilters.jsx: Estado, Cidade, Tipo de análise) e o painel
 * inferior (MapaFornecedor/FilterByType.jsx: área, preço, padrão, bairro,
 * incorporadora, fase de obra, material) — migrando os inputs para PrimeReact.
 *
 * Os valores de padrão/fase/bairros/material/incorporadoras continuam sendo objetos
 * {value,label}, formato exigido por TablePerPattern.preparePayload (reusado sem alteração).
 */
class FormFiltersFornecedor extends Component {
    state = {
        materiaisOptions: [],
        // Sugestões dos AutoComplete (preenchidas sob demanda pelo completeMethod).
        cidadesSuggestions: [],
        incorporadorasSuggestions: [],
    };

    componentDidMount() {
        this.loadMateriais();
    }

    // Lista de insumos básicos usada no select de Material.
    loadMateriais = async () => {
        try {
            const response = await Axios.get('/supplies-basic');
            const materiaisOptions = (response.data || []).map((description) => ({
                value: description,
                label: description,
            }));
            this.setState({ materiaisOptions });
        } catch (e) {
            console.error('[FormFiltersFornecedor] erro ao carregar materiais:', e);
        }
    };

    // O AutoComplete não esconde o que já foi escolhido (o react-select do mapa Google
    // escondia), então as sugestões já vêm sem os itens selecionados — do contrário dá
    // para repetir a mesma cidade/incorporadora no filtro.
    semSelecionados = (options, selecionados) => {
        const escolhidos = new Set((selecionados || []).map((item) => item.value));

        return options.filter((option) => !escolhidos.has(option.value));
    };

    // Busca de cidades (≥2 caracteres); restrita à UF quando um estado foi escolhido.
    searchCidade = async (event) => {
        const inputQuery = event.query;
        const uf = this.props.filters.estado;
        const url = uf
            ? `/cidades/json-options-by-state/${uf}/${inputQuery}`
            : `/cidades/json-options/${inputQuery}`;

        try {
            const response = await Axios.get(url);
            this.setState({
                cidadesSuggestions: this.semSelecionados(response.data || [], this.props.filters.cidades),
            });
        } catch (e) {
            console.error('[FormFiltersFornecedor] erro na busca de cidades:', e);
            this.setState({ cidadesSuggestions: [] });
        }
    };

    searchIncorporadora = async (event) => {
        try {
            const response = await Axios.get(`/autocomplete-incorporator-with-id?search=${event.query}`);
            const options = (response.data || []).map((incorporator) => ({
                value: incorporator.id,
                label: incorporator.text,
            }));
            this.setState({
                incorporadorasSuggestions: this.semSelecionados(options, this.props.filters.incorporators),
            });
        } catch (e) {
            console.error('[FormFiltersFornecedor] erro na busca de incorporadoras:', e);
            this.setState({ incorporadorasSuggestions: [] });
        }
    };

    render() {
        const { filters, bairrosOptions, onChange, onEstadoChange, onCidadesChange, onSubmit, onClear, loading } = this.props;
        const { materiaisOptions, cidadesSuggestions, incorporadorasSuggestions } = this.state;

        return (
            <div className="fornecedor-mapbox-filtros">
                <div className="form-group">
                    <label>Estado</label>
                    <Dropdown
                        options={estados}
                        value={filters.estado}
                        onChange={(e) => onEstadoChange(e.value)}
                        style={{ width: '100%' }}
                        placeholder="Selecione"
                        filter
                        showClear
                    />
                </div>

                <div className="form-group">
                    <label>Cidade</label>
                    <AutoComplete
                        multiple
                        field="label"
                        minLength={2}
                        placeholder={filters.cidades.length ? '' : 'Digite ao menos 2 caracteres'}
                        value={filters.cidades}
                        suggestions={cidadesSuggestions}
                        completeMethod={this.searchCidade}
                        onChange={(e) => onCidadesChange(e.value || [])}
                        inputStyle={{ width: '100%' }}
                        style={{ width: '100%' }}
                    />
                </div>

                <div className="form-group">
                    <label>Tipo de análise</label>
                    <Dropdown
                        options={TIPOS_ANALISE}
                        value={filters.tipoAnalise}
                        onChange={(e) => onChange('tipoAnalise', e.value)}
                        style={{ width: '100%' }}
                        placeholder="Selecione"
                        showClear
                    />
                </div>

                {filters.tipoAnalise && (
                    <>
                        <hr />

                        <div className="form-group row">
                            <div className="col-6">
                                <label>Área Mínima</label>
                                <InputNumber
                                    value={filters.area_maior_que}
                                    onValueChange={(e) => onChange('area_maior_que', e.value)}
                                    inputStyle={{ width: '100%' }}
                                    suffix=" m²"
                                    min={0}
                                />
                            </div>
                            <div className="col-6">
                                <label>Área Máxima</label>
                                <InputNumber
                                    value={filters.area_menor_que}
                                    onValueChange={(e) => onChange('area_menor_que', e.value)}
                                    inputStyle={{ width: '100%' }}
                                    suffix=" m²"
                                    min={0}
                                />
                            </div>
                        </div>

                        <div className="form-group row">
                            <div className="col-6">
                                <label>Preço Mínimo</label>
                                <InputNumber
                                    value={filters.ticket_maior_que}
                                    onValueChange={(e) => onChange('ticket_maior_que', e.value)}
                                    mode="currency"
                                    currency="BRL"
                                    locale="pt-BR"
                                    inputStyle={{ width: '100%' }}
                                    min={0}
                                />
                            </div>
                            <div className="col-6">
                                <label>Preço Máximo</label>
                                <InputNumber
                                    value={filters.ticket_menor_que}
                                    onValueChange={(e) => onChange('ticket_menor_que', e.value)}
                                    mode="currency"
                                    currency="BRL"
                                    locale="pt-BR"
                                    inputStyle={{ width: '100%' }}
                                    min={0}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Padrão</label>
                            <Dropdown
                                options={PADROES}
                                optionLabel="label"
                                value={filters.pattern}
                                onChange={(e) => onChange('pattern', e.value)}
                                style={{ width: '100%' }}
                                placeholder="Selecione"
                                showClear
                            />
                        </div>

                        <div className="form-group">
                            <label>Bairro</label>
                            <MultiSelect
                                resetFilterOnHide
                                options={bairrosOptions}
                                optionLabel="label"
                                value={filters.bairros}
                                onChange={(e) => onChange('bairros', e.value)}
                                style={{ width: '100%' }}
                                placeholder={bairrosOptions.length ? 'Selecione' : 'Selecione uma cidade'}
                                filter
                                display="chip"
                                showClear
                                maxSelectedLabels={3}
                                selectedItemsLabel="{0} itens selecionados"
                                emptyFilterMessage="Nenhum resultado encontrado"
                            />
                        </div>

                        <div className="form-group">
                            <label>Incorporadora</label>
                            <AutoComplete
                                multiple
                                field="label"
                                minLength={2}
                                placeholder={filters.incorporators.length ? '' : 'Digite ao menos 2 caracteres'}
                                value={filters.incorporators}
                                suggestions={incorporadorasSuggestions}
                                completeMethod={this.searchIncorporadora}
                                onChange={(e) => onChange('incorporators', e.value || [])}
                                inputStyle={{ width: '100%' }}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div className="form-group">
                            <label>Fase de obra</label>
                            <Dropdown
                                options={FASES_OBRA}
                                optionLabel="label"
                                value={filters.construction_status}
                                onChange={(e) => onChange('construction_status', e.value)}
                                style={{ width: '100%' }}
                                placeholder="Selecione"
                                showClear
                            />
                        </div>

                        <div className="form-group">
                            <label>
                                Material
                                {['Por fase de obra', 'Por Incorporadora'].includes(filters.tipoAnalise) && (
                                    <span className="fornecedor-obrigatorio"> *</span>
                                )}
                            </label>
                            <MultiSelect
                                resetFilterOnHide
                                options={materiaisOptions}
                                optionLabel="label"
                                value={filters.material}
                                onChange={(e) => onChange('material', e.value)}
                                style={{ width: '100%' }}
                                placeholder="Selecione"
                                filter
                                display="chip"
                                showClear
                                maxSelectedLabels={3}
                                selectedItemsLabel="{0} itens selecionados"
                                emptyFilterMessage="Nenhum resultado encontrado"
                            />
                        </div>
                    </>
                )}

                <div className="fornecedor-mapbox-filtros-actions">
                    <Button
                        label={loading ? 'Buscando…' : 'Buscar'}
                        icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
                        onClick={onSubmit}
                        disabled={loading}
                    />
                    {filters.tipoAnalise && (
                        <Button
                            label="Analisar insumos"
                            icon="pi pi-table"
                            onClick={this.props.onAnalisar}
                        />
                    )}
                    <Button
                        label="Limpar"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-secondary"
                        onClick={onClear}
                    />
                </div>
            </div>
        );
    }
}

export default FormFiltersFornecedor;
